import React, { useEffect, useRef } from "react";
import {
  FiShoppingBag,
  FiDollarSign,
  FiUsers,
  FiTruck,
  FiTrendingUp,
  FiAlertCircle,
  FiCalendar,
  FiRefreshCw,
} from "react-icons/fi";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Chart from "chart.js/auto";

const VendorDashboard = () => {
  const salesChartRef = useRef(null);
  const revenueChartRef = useRef(null);
  const salesChartInstance = useRef(null);
  const revenueChartInstance = useRef(null);

  const stats = [
    {
      title: "Total Sales",
      value: "₹1,42,459",
      change: "+12%",
      icon: <FiShoppingBag className="text-blue-500" />,
    },
    {
      title: "Total Revenue",
      value: "₹89,420",
      change: "+8%",
      icon: <FiDollarSign className="text-green-500" />,
    },
    {
      title: "New Customers",
      value: "1,248",
      change: "+5%",
      icon: <FiUsers className="text-purple-500" />,
    },
    {
      title: "Pending Orders",
      value: "56",
      change: "-3%",
      icon: <FiTruck className="text-yellow-500" />,
    },
  ];

  const recentOrders = [
    {
      id: "#ORD-001",
      customer: "Rahul Sharma",
      amount: "₹2,499",
      status: "Shipped",
      date: "12 May 2023",
    },
    {
      id: "#ORD-002",
      customer: "Priya Patel",
      amount: "₹1,799",
      status: "Processing",
      date: "11 May 2023",
    },
    {
      id: "#ORD-003",
      customer: "Amit Singh",
      amount: "₹3,299",
      status: "Delivered",
      date: "10 May 2023",
    },
    {
      id: "#ORD-004",
      customer: "Neha Gupta",
      amount: "₹1,299",
      status: "Cancelled",
      date: "09 May 2023",
    },
    {
      id: "#ORD-005",
      customer: "Vikram Joshi",
      amount: "₹2,999",
      status: "Shipped",
      date: "08 May 2023",
    },
  ];

  const topProducts = [
    { name: "Men's Cotton T-Shirt", sales: 142, revenue: "₹42,600" },
    { name: "Women's Jeans", sales: 98, revenue: "₹49,000" },
    { name: "Kids Summer Dress", sales: 76, revenue: "₹22,800" },
    { name: "Sports Shoes", sales: 65, revenue: "₹45,500" },
    { name: "Winter Jacket", sales: 54, revenue: "₹64,800" },
  ];

  // Chart data
  const salesData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    datasets: [
      {
        label: "Sales (₹)",
        data: [4000, 3000, 5000, 2780, 1890, 2390, 3490],
        backgroundColor: "rgba(59, 130, 246, 0.2)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 2,
        tension: 0.4,
        fill: true
      }
    ]
  };

  const revenueData = {
    labels: ["Men's Clothing", "Women's Clothing", "Kids", "Accessories", "Other"],
    datasets: [{
      data: [42, 35, 15, 8, 5],
      backgroundColor: [
        "rgba(59, 130, 246, 0.7)",
        "rgba(236, 72, 153, 0.7)",
        "rgba(234, 179, 8, 0.7)",
        "rgba(139, 92, 246, 0.7)",
        "rgba(156, 163, 175, 0.7)"
      ],
      borderColor: [
        "rgba(59, 130, 246, 1)",
        "rgba(236, 72, 153, 1)",
        "rgba(234, 179, 8, 1)",
        "rgba(139, 92, 246, 1)",
        "rgba(156, 163, 175, 1)"
      ],
      borderWidth: 1
    }]
  };

  useEffect(() => {
    // Initialize sales chart
    if (salesChartRef.current) {
      if (salesChartInstance.current) {
        salesChartInstance.current.destroy();
      }
      salesChartInstance.current = new Chart(salesChartRef.current, {
        type: "line",
        data: salesData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `Sales: ₹${context.raw.toLocaleString()}`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return '₹' + value.toLocaleString();
                }
              }
            }
          }
        }
      });
    }

    // Initialize revenue chart
    if (revenueChartRef.current) {
      if (revenueChartInstance.current) {
        revenueChartInstance.current.destroy();
      }
      revenueChartInstance.current = new Chart(revenueChartRef.current, {
        type: "doughnut",
        data: revenueData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "right",
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.label}: ${context.raw}%`;
                }
              }
            }
          },
          cutout: "70%"
        }
      });
    }

    // Cleanup function
    return () => {
      if (salesChartInstance.current) {
        salesChartInstance.current.destroy();
      }
      if (revenueChartInstance.current) {
        revenueChartInstance.current.destroy();
      }
    };
  }, []);

  // Custom tooltip component
  const TooltipHorizon = ({ content, placement, trigger }) => {
    return (
      <div className="relative inline-block">
        <div data-tooltip-id="vendor-tooltip" data-tooltip-content={content}>
          {trigger}
        </div>
        <ReactTooltip
          id="vendor-tooltip"
          place={placement || "top"}
          effect="solid"
          className="z-50"
        />
      </div>
    );
  };

  return (
    <div className="p-6">
      <h1 className="mb-6 text-2xl font-bold text-gray-800">
        Vendor Dashboard Overview
      </h1>

      {/* Stats Widgets */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="flex items-center rounded-lg bg-white p-6 shadow"
          >
            <div className="mr-4 rounded-full bg-gray-100 p-3">{stat.icon}</div>
            <div>
              <p className="text-sm text-gray-500">{stat.title}</p>
              <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
              <div className="mt-1 flex items-center">
                <span
                  className={`text-sm ${
                    stat.change.startsWith("+")
                      ? "text-green-500"
                      : "text-red-500"
                  }`}
                >
                  {stat.change}
                </span>
                <span className="ml-1 text-sm text-gray-500">
                  vs last month
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Sales Chart */}
        <div className="rounded-lg bg-white p-6 shadow lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              Sales Overview
            </h2>
            <TooltipHorizon
              content="Last 30 days sales data"
              placement="top"
              trigger={
                <button className="text-gray-400 hover:text-gray-600">
                  <FiCalendar />
                </button>
              }
            />
          </div>
          <div className="h-64">
            <canvas ref={salesChartRef} />
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              Revenue Breakdown
            </h2>
            <TooltipHorizon
              content="Revenue by product category"
              placement="top"
              trigger={
                <button className="text-gray-400 hover:text-gray-600">
                  <FiTrendingUp />
                </button>
              }
            />
          </div>
          <div className="h-64">
            <canvas ref={revenueChartRef} />
          </div>
        </div>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Orders */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Recent Orders
              </h2>
              <TooltipHorizon
                content="Last 5 orders"
                placement="top"
                trigger={
                  <button className="text-gray-400 hover:text-gray-600">
                    <FiRefreshCw />
                  </button>
                }
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {recentOrders.map((order, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {order.id}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {order.customer}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {order.amount}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                          order.status === "Delivered"
                            ? "bg-green-100 text-green-800"
                            : order.status === "Shipped"
                            ? "bg-blue-100 text-blue-800"
                            : order.status === "Processing"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Products */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Top Selling Products
              </h2>
              <TooltipHorizon
                content="Top 5 products by revenue"
                placement="top"
                trigger={
                  <button className="text-gray-400 hover:text-gray-600">
                    <FiAlertCircle />
                  </button>
                }
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Sales
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {topProducts.map((product, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {product.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {product.sales}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {product.revenue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Additional Widgets */}
      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Inventory Alerts */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              Inventory Alerts
            </h2>
            <span className="rounded-full bg-red-100 px-2 py-1 text-xs text-red-800">
              3 Low Stock
            </span>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Men's Cotton T-Shirt (Blue)</p>
                <p className="text-sm text-gray-500">Stock: 5 items</p>
              </div>
              <button className="text-sm text-blue-600 hover:text-blue-800">
                Reorder
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Women's Jeans (Black)</p>
                <p className="text-sm text-gray-500">Stock: 3 items</p>
              </div>
              <button className="text-sm text-blue-600 hover:text-blue-800">
                Reorder
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Kids Summer Dress (Pink)</p>
                <p className="text-sm text-gray-500">Stock: 2 items</p>
              </div>
              <button className="text-sm text-blue-600 hover:text-blue-800">
                Reorder
              </button>
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">
            Recent Activities
          </h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="mr-3 rounded-full bg-blue-100 p-2">
                <FiUsers className="text-blue-500" />
              </div>
              <div>
                <p className="font-medium">New vendor registered</p>
                <p className="text-sm text-gray-500">
                  Fashion Trends Inc. registered 2 hours ago
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="mr-3 rounded-full bg-green-100 p-2">
                <FiShoppingBag className="text-green-500" />
              </div>
              <div>
                <p className="font-medium">New order placed</p>
                <p className="text-sm text-gray-500">
                  Order #ORD-006 for ₹3,499 placed by Sanjay Verma
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="mr-3 rounded-full bg-yellow-100 p-2">
                <FiTruck className="text-yellow-500" />
              </div>
              <div>
                <p className="font-medium">Order shipped</p>
                <p className="text-sm text-gray-500">
                  Order #ORD-001 has been shipped to Rahul Sharma
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDashboard;